import React, { useMemo, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { useMapEvents } from 'react-leaflet';
import { useTours } from 'features/Responder/features/Tours/contexts/TourContext';

const menuStyles = {
  position: 'absolute',
  zIndex: 700,
  minWidth: 220,
  boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
  border: '1px solid rgba(0,0,0,0.15)',
};

/**
 * Tour-tour specific map context menu. Allows builders to quickly pick a tool without
 * exposing the zone actions from the Responder sandbox.
 */
/**
 * tour map context menu.
 * @param {object} props
 * @param {boolean} props.showTourTools
 * @param {(next: boolean) => void} props.setShowTourTools
 * @param {boolean} props.showZoneAnchors
 * @param {(next: boolean) => void} props.setShowZoneAnchors
 * @returns {JSX.Element|null}
 */
export default function TourContextMenu({
  showTourTools,
  setShowTourTools,
  showZoneAnchors,
  setShowZoneAnchors,
}) {
  const { setMapTool } = useTours();
  const [menuState, setMenuState] = useState(null);

  const map = useMapEvents({
    contextmenu: (e) => {
      e.originalEvent?.preventDefault?.();
      const pt = map.latLngToContainerPoint(e.latlng);
      setMenuState({
        latlng: e.latlng,
        point: pt,
      });
    },
    click: (e) => {
      const target = e?.originalEvent?.target;
      if (target?.closest && target.closest('.tour-context-menu')) {
        return;
      }
      setMenuState(null);
    },
    keydown: (e) => {
      if (e.originalEvent?.key === 'Escape') {
        setMenuState(null);
      }
    },
  });

  const positionStyle = useMemo(() => {
    if (!menuState) return { display: 'none' };
    return { left: menuState.point.x, top: menuState.point.y };
  }, [menuState]);

  if (!menuState) return null;

  const handleCheckpointTool = (geometry) => {
    setMapTool?.({ mode: 'checkpoint', geometry });
    setMenuState(null);
  };

  const handleStartTool = () => {
    setMapTool?.({ mode: 'start' });
    setMenuState(null);
  };

  return (
    <Card
      className="tour-context-menu"
      style={{ ...menuStyles, ...positionStyle }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <Card.Body className="p-2 d-flex flex-column gap-2">
        <div className="fw-semibold small text-uppercase text-muted">Map actions</div>
        <div className="d-flex flex-column gap-1">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => handleCheckpointTool('circle')}
          >
            Place circle checkpoint
          </Button>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => handleCheckpointTool('polygon')}
          >
            Place polygon checkpoint
          </Button>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => handleCheckpointTool('crossline')}
          >
            Place crossline checkpoint
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={handleStartTool}>
            Set start location
          </Button>
        </div>
        <div className="d-flex justify-content-between align-items-center mt-1">
          <span className="small text-muted">Show Tour tools</span>
          <Button
            size="sm"
            variant={showTourTools ? 'primary' : 'outline-secondary'}
            onClick={() => {
              setShowTourTools?.(!showTourTools);
              setMenuState(null);
            }}
          >
            {showTourTools ? 'Hide' : 'Show'}
          </Button>
        </div>
        <div className="d-flex justify-content-between align-items-center mt-1">
          <span className="small text-muted">Show Zone Anchors</span>
          <Button
            size="sm"
            variant={showZoneAnchors ? 'primary' : 'outline-secondary'}
            onClick={() => {
              setShowZoneAnchors?.(!showZoneAnchors);
              setMenuState(null);
            }}
          >
            {showZoneAnchors ? 'Hide' : 'Show'}
          </Button>
        </div>
        <div className="small text-muted">
          After selecting a tool, left-click on the map to place it.
        </div>
      </Card.Body>
    </Card>
  );
}
